import type { ShelemHandResult } from "@bing/engine-shelem";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Card, SMALL_CARD_BOX } from "../components/Card";
import { atY } from "../game/felt-geometry";
import { CrownIcon, HakemChip } from "../game/hakem-chip";
import { twoGraphemesShortfall } from "../game/name-room";
import { allTwelve, signedDelta } from "../game/shelem-hand-result";
import { BANNER_PILL } from "../game/table-notice";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { CENTRE_AIR_PX, CENTRE_GAP_MAX, CENTRE_WIDE_MAX, seatRootHeight } from "./shelem-auction";
import { FELT_INSET, PARTNER_TOP_IN_FELT } from "./shelem-felt";

/* ------------------------------------------------------------------ *
 * THE HAND-END CARD (P1-7, P1 Task 7; card-67 C5) — on the felt once the 12th trick has been
 * COLLECTED, for `HAND_END_CARD_MS` (never at the match's end since card-98 item 11), before the next auction
 * speaks (the engine's hand-end beat waits for it). The operator (C5): «End-of-hand card: too busy and
 * gone too fast. Keep only the useful information, show it bigger in the middle of the table between
 * hands, and keep it a few seconds longer». What it says:
 *   · how the contract went — the bid in its chip (a call's line names the call), then the outcome's
 *     words in the حاکم's team colour, bouncing in: «قرارداد شد» / «کم آورد» (a set or a yasa) /
 *     «شلم» / «شلم خوانده»; ALL TWELVE (a sweep, a call made) wear the hand's gold banner pill (P1-8);
 *   · the حاکم — its crown chip and name — and the four it laid down, REVEALED now, never before
 *     (the engine's `lastHand.discards`) — the operator, after C5: «Bring back the 4 cards the حاکم
 *     put away and the حاکم's name»;
 *   · one row a team, yours first, in its colour: what the hand gave it, signed;
 *   · the target only where the header's score box draws none (its tight form) — and the middle holds
 *     the line (`HandEndPlacement.target`).
 * What it does not say: the match's totals («مجموع» — the operator: «the score box at the top right
 * already shows them after each hand»; it moves WITH the card), and the per-team breakdown — card
 * points, tricks × 5, the کف — whose sum was the row's. With one number a team, the rows carry no
 * column heads. BIGGER: the result line 16 px (was 14), the points 17 px (the breakdown was 11), the
 * names 13 (were 11). Numbers come ONLY from `lastHand` and the view.
 * WHERE it sits is measured off `main`, as the auction's centre: IN THE MIDDLE of the felt — centred
 * on it, between the side seats (whose columns are centred on it too), never above the partner's
 * column nor into the felt's 12 px foot — no wider than the gap between the side columns
 * (`CENTRE_GAP_MAX`), so it never meets a seat. Where its rows run wider than that gap (the operator's
 * 384 px phone under a 150 % zoom) the card draws TIGHT — each size a step down, measured in a layout
 * effect, so no frame paints a row out of the card. Where not even the felt holds it (Telegram
 * Desktop's best-effort sizes) it lies centred OVER the seats (z-30 above their z-20: they wait out
 * the card), the felt's width less its corners. The table decides WHEN (`game/shelem-moments.ts`).
 * card-98 (the operator, 2026-10-27): item 10 — the four the حاکم laid down are the card's TOP LINE now,
 * at a readable size (they were 20×28 beside the name); item 13c — where that card is taller than the
 * felt's middle (Telegram Desktop 307×472, where it lay over both side seats) it draws BESIDE: the four
 * beside the words, the points on one row, small enough to stay between the seats (`HandEndLayout`).
 * ------------------------------------------------------------------ */

/** The revealed کف: four `small` faces (36×50) — card-98 item 10, the operator: «the حاکم's 4 cards are
 *  too small — move them to the top line, readable» (they were the face-down کف's 20×28) — at this
 *  scale: the STACKED card's top line ×1.25 (45×62.5, the size of the cards played on the felt), the
 *  BESIDE card's ×1 (36×50: the short felt's middle holds no more); overlapped so each card's index
 *  column stays whole. */
const KAF_SCALE = { stacked: 1.25, beside: 1 } as const;
const kafCardW = (layout: HandEndLayout): number => SMALL_CARD_BOX.w * KAF_SCALE[layout];
const kafCardH = (layout: HandEndLayout): number => SMALL_CARD_BOX.h * KAF_SCALE[layout];
/** The step between two revealed cards — the stacked top line's, and 4 px closer in a TIGHT card… */
const KAF_REVEAL_STEP = 20;
const KAF_REVEAL_STEP_TIGHT = 16;
/** …and the BESIDE card's, each card's index still clear of the next — 8 where its column overflows
 *  (the index column of a `small` face is 7 px wide). */
const KAF_REVEAL_STEP_BESIDE = 12;
const KAF_REVEAL_STEP_BESIDE_TIGHT = 8;
/** The TIGHT card's crown, alone where the roomy card wears the «حاکم» chip (card-67 D). */
const TIGHT_CROWN_PX = 14;
/** The card's foot: 12 px off the felt's bottom, clear of its rounded corners' ring. */
const CARD_FOOT_PX = 12;
/** …a design margin, not the ring's: the gold ring is a 0-offset spread OUTSIDE the felt's box, so
 *  the card may run into the foot down to 1 px of that box (the probe's `*-felt-edge` rule). */
const FELT_EDGE_AIR_PX = 1;

/** The BESIDE card's words — the line of the bid's chip and the outcome — painted ONTO THE FOUR: the
 *  outcome's nowrap words run out of the narrow column toward them, INSIDE the card, where the card's own
 *  width never overflows (the layout probes, card-98: «شلم خوانده» up to 30.8 px onto the 4th card at
 *  300×667 ×1.5, «قرارداد شد» 20.6) — so it is asked of the boxes themselves: the LINE's, which is as
 *  wide as its widest word (the column draws it at its own width), never the outcome's, which bounces
 *  in (`pop-in-glow`: 20 px low at 80 % when the card mounts). The card's own entrance scales both. */
function wordsOnKaf(line: HTMLElement | null, kaf: HTMLElement | null): boolean {
  if (line === null || kaf === null) return false;
  const r = line.getBoundingClientRect();
  const k = kaf.getBoundingClientRect();
  return (
    Math.min(r.right, k.right) - Math.max(r.left, k.left) > 0 &&
    Math.min(r.bottom, k.bottom) - Math.max(r.top, k.top) > 0
  );
}

/** The card taller than its ROOM on the felt — from where it sits to 1 px of the felt's box (its wrapper
 *  is the felt's child) — read off LAYOUT boxes, which its entrance's scale never touches, with 1 px for
 *  their rounding. Unlaid-out (happy-dom, a hidden page): never. */
function outgrowsRoom(card: HTMLElement | null): boolean {
  const wrap = card?.parentElement;
  const felt = wrap?.offsetParent;
  if (!card || !wrap || !(felt instanceof HTMLElement) || felt.clientHeight === 0) return false;
  return card.offsetHeight + 1 > felt.clientHeight - FELT_EDGE_AIR_PX - wrap.offsetTop;
}

/** A `leading-tight` line of `px` text at a text zoom. */
const line = (px: number, zoom: number): number => px * 1.25 * zoom;

/**
 * The card's two ARRANGEMENTS (card-98 items 10 and 13c):
 *  · `stacked` — the revealed کف ALONE on the card's top line, centred, then the result, the حاکم, the
 *    points: where the felt's middle holds it (every phone, at every text zoom);
 *  · `beside` — the کف beside a short column (the bid and the outcome, then the crown and the name), the
 *    points on ONE row under them, at the tight sizes: where the stacked card is taller than the felt's
 *    middle (Telegram Desktop's 307×472, whose middle is 163 × 98 px between the seats) — so the card
 *    stays between the seats instead of lying over them. Where its column is too narrow for the
 *    outcome's words (phones of 300–384 px under a text zoom: they ran onto the four), the four step
 *    closer, and where that is not enough the outcome takes a line of its own under them, centred
 *    (card-98's kit fix, measured on the page — `wordsOnKaf`; the bid and the name stay beside the
 *    four, which are as tall as both: the card grows by that one line less the column's outcome, and
 *    gives up the target's line) — only where the card still fits its room on the felt
 *    (`outgrowsRoom`; the narrow panels of 270–290 px keep the words beside, as before).
 */
export type HandEndLayout = "stacked" | "beside";

/**
 * The card's height at a text zoom (CSS px), from its own rows (an upper bound):
 *  · stacked, at its ROOMY sizes (the tight ones are smaller): `py-2`; the revealed کف's 62.5 px; 6 px;
 *    the result row (the gold pill for all twelve — `BANNER_PILL`'s `py-1.5` and its 16 px label line —
 *    else the 16 px line); 6 px; the حاکم's row — its 13 px name line; 6 px; the points — two rows of
 *    the 17 px points, 2 px apart; and the target's 11 px line and its 6 px where it is drawn;
 *  · beside, at the TIGHT sizes it always draws: `py-2`; the taller of the کف's 50 px and the column —
 *    the bid's 12 px chip line (and its `py-px`), 2 px, the outcome's 14 px line (the pill for all
 *    twelve), 2 px, the name's 12 px line; 6 px; the one points row's 15 px line; the target as above.
 */
export function handEndCardHeight(
  zoom: number,
  slam: boolean,
  target = false,
  layout: HandEndLayout = "stacked",
): number {
  const targetLine = target ? 6 + line(11, zoom) : 0;
  if (layout === "beside") {
    const result = slam ? 12 + 16 * zoom : line(14, zoom);
    const column = line(12, zoom) + 2 + 2 + result + 2 + line(12, zoom);
    return 2 * 8 + Math.max(kafCardH("beside"), column) + 6 + line(15, zoom) + targetLine;
  }
  const result = slam ? 12 + 16 * zoom : line(16, zoom);
  const hakem = line(13, zoom);
  const points = 2 * line(17, zoom) + 2;
  return 2 * 8 + kafCardH("stacked") + 6 + result + 6 + hakem + 6 + points + targetLine;
}

/** Where the card sits (felt coordinates) and how wide it may be: in the middle, or — where the felt
 *  cannot hold it under the partner — over the seats; how it is arranged; and whether it draws the
 *  target's line. */
export interface HandEndPlacement {
  readonly form: "middle" | "over";
  /** Stacked (the کف on its own top line) or beside (the کف beside the words) — see `HandEndLayout`.
   *  Absent: stacked. */
  readonly layout?: HandEndLayout;
  readonly top: string;
  readonly maxWidth: string;
  /** The target's line is drawn: asked for (the header's tight form) AND held where the card sits —
   *  in the middle the card gives the line up before it would go over the seats. */
  readonly target: boolean;
}

/**
 * WHERE THE HAND-END CARD SITS (card-67 C5: «in the middle of the table») — measured off `main`'s
 * laid-out height: centred on the felt — the side seats' middle — clamped under the partner's column
 * (its floor: the partner's root + 4 px) and over the felt's 12 px foot, no wider than the gap between
 * the side columns (`middle`); where the foot is short but the felt is not, at the partner's floor,
 * down to 1 px of the felt's box. The target's line (`target`, asked where the header's box draws
 * none) is the first thing the middle gives up: at 320×568 under a 150 % zoom the card holds the
 * middle without it, and over the seats it would cover them. Where the STACKED card (the revealed کف
 * on its own top line, card-98 item 10) is taller than the middle, the BESIDE one (card-98 item 13c:
 * Telegram Desktop's 307×472, where the card lay over both side seats) — in the same order. Else over
 * the seats, centred, as wide as the felt less its corners (`over`), BESIDE — the smaller card, which a
 * short felt's own box clips least. Unmeasured (happy-dom, a
 * hidden page): stacked, in the middle form at the partner's floor.
 */
export function handEndCardPlacement(
  mainH: number | null,
  compact: boolean,
  zoom: number,
  slam: boolean,
  target = false,
): HandEndPlacement {
  const floor = PARTNER_TOP_IN_FELT + seatRootHeight(compact, zoom) + CENTRE_AIR_PX;
  if (mainH === null)
    return {
      form: "middle",
      layout: "stacked",
      top: `${floor.toFixed(1)}px`,
      maxWidth: CENTRE_GAP_MAX,
      target,
    };
  const feltH = mainH - 2 * FELT_INSET;
  const bottom = feltH - CARD_FOOT_PX;
  for (const layout of ["stacked", "beside"] as const)
    for (const line of target ? [true, false] : [false]) {
      const h = handEndCardHeight(zoom, slam, line, layout);
      if (bottom - floor >= h) {
        const top = Math.min(Math.max(floor, feltH / 2 - h / 2), bottom - h);
        return {
          form: "middle",
          layout,
          top: `${top.toFixed(1)}px`,
          maxWidth: CENTRE_GAP_MAX,
          target: line,
        };
      }
      if (feltH - floor - FELT_EDGE_AIR_PX >= h)
        return {
          form: "middle",
          layout,
          top: `${floor.toFixed(1)}px`,
          maxWidth: CENTRE_GAP_MAX,
          target: line,
        };
    }
  // over the seats: the SMALLER arrangement (the felt is short there — a taller card would be clipped
  // by the felt's own box, its points first: card-98 review E-I2)
  const h = handEndCardHeight(zoom, slam, target, "beside");
  return {
    form: "over",
    layout: "beside",
    top: `${Math.max(0, (feltH - h) / 2).toFixed(1)}px`,
    maxWidth: CENTRE_WIDE_MAX,
    target,
  };
}

/** The card's two sizes: ROOMY, and TIGHT where a row runs wider than the card may be — or the حاکم's
 *  name is cut below its first two letters (`twoGraphemesShortfall`) — each type size a step down, the
 *  gaps tighter, the padding 8 px, the کف at 12 px steps and the حاکم's crown ALONE for its «حاکم»
 *  chip (card-67 D, the matrix: at 300×667 under a 150 % zoom the chip's 41 px left a 16-letter Latin
 *  name 21 px — «M…»; the crown is the chip's own mark, `CrownIcon`). Where even that leaves the name
 *  short, the tight card is still the one with the most of it — so ANY shortfall turns it tight (card-a7
 *  B10's bound — tight only where its 10 px sufficed — went with the crown). */
const SIZES = {
  roomy: {
    card: "px-3 py-2 gap-1.5",
    chip: "text-[13px]",
    result: "text-[16px]",
    hakem: "gap-1",
    name: "text-[13px]",
    grid: "gap-x-3 gap-y-0.5",
    team: "text-[13px]",
    points: "text-[17px]",
    target: "text-[11px]",
  },
  tight: {
    card: "px-2 py-2 gap-1.5",
    chip: "text-[12px]",
    result: "text-[14px]",
    hakem: "gap-0.5",
    name: "text-[12px]",
    // 8 px between a team's word and its points, not less (card-67 C5's tight card at ×1.5)
    grid: "gap-x-2 gap-y-0.5",
    team: "text-[12px]",
    points: "text-[15px]",
    target: "text-[10px]",
  },
} as const;

export function HandEndCard({
  last,
  you,
  targetScore,
  hakemName,
  placement,
}: {
  /** The finished hand — the frame's `lastHand`. */
  last: ShelemHandResult;
  you: number;
  targetScore: number;
  /** The حاکم's name as this viewer reads it. */
  hakemName: string;
  /** Where it sits — and whether it draws the target's line (`target`: the header's box draws none). */
  placement: HandEndPlacement;
}): ReactNode {
  const myTeam = you % 2;
  const declTeam = last.declaringTeam;
  const tone = (team: number): string => (team === myTeam ? "text-primary" : "text-error");
  const slam = allTwelve(last);
  // TIGHT where a row runs out of the card's own box (the gap between the side seats: the 384 px phone
  // under a 150 % zoom), or where the حاکم's name is cut below its first two letters — measured in a
  // LAYOUT effect, so no frame paints a row out of the card, and again when a web font lands, the window
  // resizes or the NAME's own box changes after that pass (a ResizeObserver, card-67 D: the probes' text
  // zoom rescales an inline size — the «حاکم» chip's 9 px — a beat after the mount, which shrank the
  // name below its two letters with no event to re-read it); latched for the card's life, so it never
  // measures its way back and flickers
  const cardRef = useRef<HTMLDivElement | null>(null);
  const nameRef = useRef<HTMLSpanElement | null>(null);
  const wordsRef = useRef<HTMLDivElement | null>(null);
  const kafRef = useRef<HTMLSpanElement | null>(null);
  const [tight, setTight] = useState(false);
  // …and the BESIDE card's words on its four (card-98's kit fix): first the four step closer (`tight`),
  // then, where they still meet, the outcome takes a line of its own under the four (`under`)
  const [under, setUnder] = useState(false);
  // …unless the card then outgrows its room on the felt (the narrow panels, 270–290 px: the line costs
  // more than the placement measured the card for): there the words stay beside, as before
  const [overRoom, setOverRoom] = useState(false);
  const wordsUnder = under && !overRoom;
  const layout = placement.layout ?? "stacked";
  const beside = layout === "beside";
  useLayoutEffect(() => {
    const check = (): void => {
      const el = cardRef.current;
      const name = nameRef.current;
      const onKaf = beside && wordsOnKaf(wordsRef.current, kafRef.current);
      if (!tight) {
        if (
          el !== null &&
          (el.scrollWidth > el.clientWidth + 0.5 ||
            (name !== null && twoGraphemesShortfall(name) > 0) ||
            onKaf)
        )
          setTight(true);
      } else if (onKaf && !under) setUnder(true);
      else if (under && !overRoom && outgrowsRoom(el)) setOverRoom(true);
    };
    check();
    const fonts: EventTarget | undefined = document.fonts;
    fonts?.addEventListener("loadingdone", check);
    window.addEventListener("resize", check);
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(check) : null;
    if (nameRef.current !== null) ro?.observe(nameRef.current);
    return () => {
      fonts?.removeEventListener("loadingdone", check);
      window.removeEventListener("resize", check);
      ro?.disconnect();
    };
  }, [tight, under, overRoom, beside, slam, placement.form, placement.maxWidth]);
  // the BESIDE card always draws the tight sizes (its column is narrow by design — card-98 item 13c)
  const small = tight || beside;
  const size = small ? SIZES.tight : SIZES.roomy;
  // the BESIDE card's own step down where its column overflows (an all-twelve pill — «شلم خوانده» —
  // wider than the column at 307×472): the four closer, the column wider
  const kafStep = beside
    ? tight
      ? KAF_REVEAL_STEP_BESIDE_TIGHT
      : KAF_REVEAL_STEP_BESIDE
    : tight
      ? KAF_REVEAL_STEP_TIGHT
      : KAF_REVEAL_STEP;
  // the all-twelve pill, a step down with the card (its label line the card's result size)
  const slamPill = small
    ? BANNER_PILL.replace("px-4", "px-2").replace("text-label-md", "text-[13px] leading-4")
    : BANNER_PILL;
  const cell = "m-0 whitespace-nowrap text-center leading-tight";
  // the bid in its chip (a call's line names the call) — the contract's value
  const bid = last.called ? null : (
    <span
      data-hand-end="bid"
      aria-label={fa.shelem.contractIs(faDigits(last.bid))}
      className={`m-0 shrink-0 whitespace-nowrap rounded-full bg-surface-container px-1.5 py-px font-bold leading-tight text-on-surface-variant ${size.chip}`}
    >
      {faDigits(last.bid)}
    </span>
  );
  // the outcome's words in the حاکم's team colour (all twelve: the hand's gold banner pill), bouncing in
  const outcome = (
    <span
      data-testid={slam ? "shelem-hand-banner" : "shelem-hand-result"}
      data-banner={slam ? (last.outcome === "sweep" ? "sweep" : "callMade") : undefined}
      data-outcome={last.outcome}
      role="status"
      className={
        slam
          ? `${slamPill} flex min-w-0 items-center justify-center whitespace-nowrap`
          : `m-0 whitespace-nowrap font-extrabold leading-tight ${size.result} ${tone(declTeam)}`
      }
      style={{ animation: "pop-in-glow .45s ease", ...(slam ? { fontWeight: 800 } : {}) }}
    >
      {fa.shelem.handResult[last.outcome]}
    </span>
  );
  // the حاکم: its «حاکم» chip (the crown alone in the small card) and its name
  const hakem = (
    <div
      data-hand-end="hakem"
      className={`flex min-w-0 items-center ${beside ? "w-full" : "justify-center"} ${size.hakem} font-bold leading-tight`}
    >
      {small ? (
        <span
          role="img"
          aria-label={fa.hokm.hakem}
          data-hand-end="crown"
          className="m-0 flex shrink-0 items-center text-secondary"
        >
          <CrownIcon size={TIGHT_CROWN_PX} />
        </span>
      ) : (
        <HakemChip />
      )}
      <span
        ref={nameRef}
        dir="auto"
        className={`m-0 min-w-0 truncate ${size.name} ${tone(declTeam)}`}
      >
        {hakemName}
      </span>
    </div>
  );
  // the four the حاکم laid down — revealed now, at a readable size (card-98 item 10)
  const kaf = (
    <span
      ref={kafRef}
      role="img"
      aria-label={fa.shelem.kafRevealed}
      data-hand-end="kaf"
      className="relative shrink-0"
      style={{
        width: kafCardW(layout) + (last.discards.length - 1) * kafStep,
        height: kafCardH(layout),
      }}
    >
      {last.discards.map((card, i) => (
        <span
          key={card}
          className="absolute top-0"
          style={{
            left: i * kafStep,
            transformOrigin: "top left",
            transform: KAF_SCALE[layout] === 1 ? undefined : `scale(${KAF_SCALE[layout]})`,
          }}
        >
          <Card id={card} small />
        </span>
      ))}
    </span>
  );
  // the target's line — given up where the outcome went under the four (that line costs one more:
  // 320×568 ×1.5's «شلم خوانده» card then reached the felt's edge), as the placement gives it up first
  const target =
    placement.target && !wordsUnder ? (
      <span
        data-hand-end="target"
        className={`${cell} font-normal text-on-surface-variant ${size.target}`}
      >
        {fa.shelem.playedTo(faDigits(targetScore))}
      </span>
    ) : null;
  return (
    <div
      className="pointer-events-none absolute inset-x-0 z-30 flex justify-center"
      style={atY(placement.top)}
      data-hand-end-form={placement.form}
      data-hand-end-layout={layout}
    >
      <div
        ref={cardRef}
        data-testid="shelem-hand-end"
        data-hand-end-tight={tight ? "" : undefined}
        data-hand-end-words={wordsUnder ? "under" : undefined}
        role="group"
        aria-label={fa.shelem.handEnd}
        className={`flex min-w-0 flex-col items-stretch rounded-2xl bg-surface-container-highest shadow-lg ${size.card}`}
        style={{
          maxWidth: placement.maxWidth,
          boxShadow: "inset 0 2px 6px rgba(0,0,0,.4), 0 8px 24px rgba(0,0,0,.45)",
          animation: "bg-pop .25s ease-out",
        }}
      >
        {beside ? (
          <>
            {/* BESIDE (a short felt): the words in a column — the bid and the outcome (on two lines
                where they do not fit across), the crown and the name — and the four beside them */}
            <div data-hand-end="top" className="flex min-w-0 items-center gap-1.5">
              <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
                <div
                  ref={wordsRef}
                  data-hand-end="line"
                  className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5"
                >
                  {bid}
                  {wordsUnder ? null : outcome}
                </div>
                {hakem}
              </div>
              {kaf}
            </div>
            {/* where the outcome still met the four 8 px apart: its own line under them, centred */}
            {wordsUnder ? (
              <div data-hand-end="words" className="flex min-w-0 justify-center">
                {outcome}
              </div>
            ) : null}
            {/* the points on ONE row: ours, then theirs */}
            <div
              data-hand-end="points"
              className="flex items-baseline justify-center gap-x-2.5 whitespace-nowrap"
            >
              {[myTeam, 1 - myTeam].map((team) => (
                <span key={team} className="flex items-baseline gap-1">
                  <span
                    data-hand-end-team={team === myTeam ? "ours" : "theirs"}
                    className={`m-0 whitespace-nowrap font-bold leading-tight ${size.team} ${tone(team)}`}
                  >
                    {team === myTeam ? fa.shelem.us : fa.shelem.them}
                  </span>
                  <span
                    dir="ltr"
                    data-hand-end="delta"
                    className={`${cell} font-extrabold ${size.points} ${tone(team)}`}
                  >
                    {signedDelta(last.teamDelta[team] ?? 0)}
                  </span>
                </span>
              ))}
            </div>
          </>
        ) : (
          <>
            {/* STACKED: the four on the card's TOP LINE (card-98 item 10), centred over the result */}
            <div data-hand-end="top" className="flex justify-center">
              {kaf}
            </div>
            {/* how the contract went — the bid in its chip, the outcome's words; the line bounces in */}
            <div className="flex min-w-0 items-center justify-center gap-1.5">
              {bid}
              {outcome}
            </div>
            {hakem}
            {/* one row a team, yours first: what this hand gave it */}
            <div
              data-hand-end="points"
              className={`grid items-baseline ${size.grid}`}
              style={{ gridTemplateColumns: "auto auto" }}
            >
              {[myTeam, 1 - myTeam].map((team) => [
                <span
                  key={`${team}-t`}
                  data-hand-end-team={team === myTeam ? "ours" : "theirs"}
                  className={`m-0 whitespace-nowrap font-bold leading-tight ${size.team} ${tone(team)}`}
                >
                  {team === myTeam ? fa.shelem.us : fa.shelem.them}
                </span>,
                <span
                  key={`${team}-d`}
                  dir="ltr"
                  data-hand-end="delta"
                  className={`${cell} font-extrabold ${size.points} ${tone(team)}`}
                >
                  {signedDelta(last.teamDelta[team] ?? 0)}
                </span>,
              ])}
            </div>
          </>
        )}
        {target}
      </div>
    </div>
  );
}
